import { 
  Grid, 
  HStack, 
  Stack, 
  Text, 
  useToast,
  Box,
  Heading,
  Avatar,
  AvatarGroup,
  Card,
  CardBody,
  CardHeader,
  SimpleGrid,
  Icon,
  VStack,
  Badge,
  Divider,
  Spinner,
  Center,
  Tooltip,
} from '@chakra-ui/react';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { getSingleDocumentFirebase } from '../../Api/firebaseApi';
import BackButtons from '../../Components/Buttons/BackButtons';
import ChartsBarKanban from '../../Components/Charts/ChartsBarKanban';
import ChartsPieKanban from '../../Components/Charts/ChartsPieKanban';
import { useTranslation } from 'react-i18next';
import useUserStore from '../../Hooks/Zustand/Store';
import { 
  FaUsers, 
  FaTasks, 
  FaClock,
  FaCalendar,
  FaTrophy,
  FaFolderOpen,
  FaCheckCircle,
} from 'react-icons/fa';
import { 
  collection, 
  query, 
  where, 
  getDocs,
  getAggregateFromServer,
  sum,
  average,
  count,
} from 'firebase/firestore';
import { db } from '../../Config/firebase';
import { msToTime } from '../../Utils/timeUtil';

function ManagementScoreTaskDetailV2() {
  const { t } = useTranslation();
  const globalState = useUserStore();
  const params = useParams();

  const [dataKanban, setDataKanban] = useState({});
  const [statistics, setStatistics] = useState({
    totalTasks: 0,
    completedTasks: 0,
    totalScore: 0,
    avgScore: 0,
    avgTime: 0,
    highestScore: 0,
    lowestScore: 0,
    tasksByColumn: {},
    tasksByLabel: {},
    tasksByCategory: {},
    topContributors: [],
  });
  const [loading, setLoading] = useState(true);
  const toast = useToast();

  // Get project name
  const project = globalState.projects?.find((p) => p.id === dataKanban?.projectId);

  // HELPER FUNCTIONS FOR SAFE NUMBER FORMATTING
  const safeNumber = (value, defaultValue = 0) => {
    const num = Number(value);
    return isNaN(num) || !isFinite(num) ? defaultValue : num;
  };

  const formatNumber = (value, decimals = 0) => {
    const num = safeNumber(value);
    return num.toFixed(decimals);
  };

  const getKanbanStatistics = async () => {
    try {
      setLoading(true);

      // Query all DONE kanban tasks for this file
      const kanbanRef = collection(db, 'kanban');
      const kanbanQuery = query(
        kanbanRef,
        where('filesId', '==', params.id),
        where('companyId', '==', globalState.currentCompany),
        where('column', '==', 'done')
      );

      // Get aggregate data for DONE tasks
      const aggregateSnapshot = await getAggregateFromServer(kanbanQuery, {
        totalScore: sum('score'),
        avgScore: average('score'),
        taskCount: count()
      });

      const aggregateData = aggregateSnapshot.data();

      // Get all DONE tasks for detailed analysis
      const tasksSnapshot = await getDocs(kanbanQuery);
      
      let totalTime = 0;
      let taskWithTime = 0;
      let totalScoreSum = 0;
      const columnCount = {};
      const labelCount = {};
      const categoryCount = {};
      const contributorMap = {};
      let highestScore = 0;
      let lowestScore = 10;

      tasksSnapshot.forEach((doc) => {
        const task = doc.data();

        // Sum up total score from done tasks - ENSURE IT'S A NUMBER
        const taskScore = safeNumber(task.score, 0);
        totalScoreSum += taskScore;

        // Track highest and lowest scores
        if (taskScore > highestScore) highestScore = taskScore;
        if (taskScore < lowestScore && taskScore > 0) lowestScore = taskScore;

        // Count by column
        columnCount[task.column] = (columnCount[task.column] || 0) + 1;

        // Count by label
        if (task.label) {
          labelCount[task.label] = (labelCount[task.label] || 0) + 1;
        }

        // Count by category
        if (task.category) {
          categoryCount[task.category] = (categoryCount[task.category] || 0) + 1;
        }

        // Track contributors
        if (task.asignee && Array.isArray(task.asignee)) {
          task.asignee.forEach((userId) => {
            if (!contributorMap[userId]) {
              contributorMap[userId] = {
                userId,
                taskCount: 0,
                totalScore: 0,
              };
            }
            contributorMap[userId].taskCount++;
            contributorMap[userId].totalScore += taskScore;
          });
        }

        // Calculate time
        if (task.time_completed_at?.seconds && task.time_backlog_at?.seconds) {
          const completedTime = task.time_completed_at.seconds;
          const backlogTime = task.time_backlog_at.seconds;
          const timeSpent = (completedTime - backlogTime) * 1000;
          
          if (timeSpent > 0) {
            totalTime += timeSpent;
            taskWithTime++;
          }
        }
      });

      // Calculate average time
      const avgTime = taskWithTime > 0 ? totalTime / taskWithTime : 0;

      // Get top contributors
      const contributors = Object.values(contributorMap)
        .map((contributor) => ({
          ...contributor,
          avgScore: contributor.taskCount > 0 ? contributor.totalScore / contributor.taskCount : 0,
        }))
        .sort((a, b) => b.taskCount - a.taskCount)
        .slice(0, 5);

      // Enrich contributors with user data
      const enrichedContributors = contributors.map((contributor) => {
        const userData = globalState.users?.find((u) => u.id === contributor.userId);
        return {
          ...contributor,
          name: userData?.name || 'Unknown',
          email: userData?.email || '',
          image: userData?.image || '',
        };
      });

      setStatistics({
        totalTasks: tasksSnapshot.size,
        completedTasks: tasksSnapshot.size,
        totalScore: safeNumber(totalScoreSum, 0),
        avgScore: safeNumber(aggregateData.avgScore, 0),
        avgTime: safeNumber(avgTime, 0),
        highestScore: safeNumber(highestScore, 0),
        lowestScore: safeNumber(lowestScore, 0),
        tasksByColumn: columnCount,
        tasksByLabel: labelCount,
        tasksByCategory: categoryCount,
        topContributors: enrichedContributors,
      });

    } catch (error) {
      console.error('Error fetching statistics:', error);
      toast({
        title: 'Error',
        description: 'Failed to fetch statistics',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const getData = async () => {
    try {
      const result = await getSingleDocumentFirebase('files', params.id);
      setDataKanban(result);
    } catch (error) {
      toast({
        title: 'Error!',
        description: error.message,
        status: 'error',
        duration: 2000,
        isClosable: true,
      });
    }
  };

  useEffect(() => {
    getData();
    return () => {
      setDataKanban({});
    };
  }, []);

  useEffect(() => {
    if (dataKanban?.id && globalState.currentCompany) {
      getKanbanStatistics();
    }
  }, [dataKanban, globalState.currentCompany]);

  const getColumnColor = (column) => {
    const colors = {
      'backlog': 'red',
      'todos': 'blue',
      'progress': 'green',
      'review': 'yellow',
      'done': 'gray',
    };
    return colors[column] || 'gray';
  };

  const getScoreColor = (score) => {
    const numScore = safeNumber(score);
    if (numScore >= 8) return 'green';
    if (numScore >= 7) return 'blue';
    if (numScore >= 6) return 'yellow';
    if (numScore >= 5) return 'orange';
    return 'red';
  };

  if (loading) {
    return (
      <Center h="100vh">
        <VStack>
          <Spinner size="xl" color="blue.500" thickness="4px" />
          <Text color="gray.500">Loading statistics...</Text>
        </VStack>
      </Center>
    );
  }

  return (
    <Stack spacing={6} p={[2, 4, 6]}>
      {/* Header Section */}
      <HStack flexWrap="wrap" spacing={3}>
        <BackButtons />
        <VStack align="start" spacing={0} flex={1}>
          <Heading size="xl" bgGradient="linear(to-r, blue.400, purple.500)" bgClip="text">
            {dataKanban?.title || 'Loading...'}
          </Heading>
          <HStack spacing={3} fontSize="sm" color="gray.500">
            <HStack>
              <Icon as={FaFolderOpen} />
              <Text>{project?.name || 'Unknown Project'}</Text>
            </HStack>
            <Divider orientation="vertical" h="15px" />
            <HStack>
              <Icon as={FaCalendar} />
              <Text>
                Updated {dataKanban?.lastUpdated?.seconds 
                  ? moment.unix(dataKanban.lastUpdated.seconds).fromNow()
                  : 'N/A'
                }
              </Text>
            </HStack>
          </HStack>
        
          <Text fontSize="xs" color="gray.400" mt={1}>
            ID: {params.id}
          </Text>
        </VStack>

        <VStack align="end" spacing={1}>
          {dataKanban?.pic?.length > 0 && (
            <VStack align="end" spacing={1}>
              <Text fontSize="xs" fontWeight="bold" color="gray.600">
                {t('leader')}:
              </Text>
              {dataKanban.pic.map((leader, index) => (
                <HStack key={index} spacing={2}>
                  <Avatar size="xs" name={leader.name} />
                  <VStack align="start" spacing={0}>
                    <Text fontSize="xs" fontWeight="medium">{leader.name}</Text>
                    <Text fontSize="2xs" color="gray.500">{leader.email}</Text>
                  </VStack>
                </HStack>
              ))}
            </VStack>
          )}
        </VStack>
      </HStack>

      {/* Key Metrics */}
      <SimpleGrid columns={[1, 2, 4]} spacing={4}>
        <Card bgGradient="linear(to-br, blue.400, blue.600)" color="white" shadow="lg">
          <CardBody>
            <HStack justify="space-between">
              <VStack align="start" spacing={0}>
                <Text fontSize="xs" opacity={0.9}>Completed Tasks</Text>
                <Heading size="xl">{statistics.totalTasks}</Heading>
                <Text fontSize="xs" opacity={0.8}>✅ Done Status</Text>
              </VStack>
              <Icon as={FaTasks} boxSize={12} opacity={0.3} />
            </HStack>
          </CardBody>
        </Card>

        <Card bgGradient="linear(to-br, green.400, green.600)" color="white" shadow="lg">
          <CardBody>
            <HStack justify="space-between">
              <VStack align="start" spacing={0}>
                <Text fontSize="xs" opacity={0.9}>All Completed</Text>
                <Heading size="xl">{statistics.completedTasks}</Heading>
                <Text fontSize="xs" opacity={0.8}>100% done ✅</Text>
              </VStack>
              <Icon as={FaCheckCircle} boxSize={12} opacity={0.3} />
            </HStack>
          </CardBody>
        </Card>

        <Card bgGradient="linear(to-br, purple.400, purple.600)" color="white" shadow="lg">
          <CardBody>
            <HStack justify="space-between">
              <VStack align="start" spacing={0}>
                <Text fontSize="xs" opacity={0.9}>Total Score</Text>
                <Heading size="xl">{formatNumber(statistics.totalScore, 0)}</Heading>
                <Text fontSize="xs" opacity={0.8}>Avg: {formatNumber(statistics.avgScore, 2)}</Text>
              </VStack>
              <Icon as={FaTrophy} boxSize={12} opacity={0.3} />
            </HStack>
          </CardBody>
        </Card>

        <Card bgGradient="linear(to-br, orange.400, orange.600)" color="white" shadow="lg">
          <CardBody>
            <HStack justify="space-between">
              <VStack align="start" spacing={0}>
                <Text fontSize="xs" opacity={0.9}>Avg Time</Text>
                <Heading size="lg">{msToTime(statistics.avgTime)}</Heading>
              </VStack>
              <Icon as={FaClock} boxSize={12} opacity={0.3} />
            </HStack>
          </CardBody>
        </Card>
      </SimpleGrid>

      {/* Tasks by Column */}
      {Object.keys(statistics.tasksByColumn).length > 0 && (
        <Card shadow="md">
          <CardHeader>
            <VStack align="start" spacing={1}>
              <Heading size="sm">📊 Task Distribution</Heading>
            </VStack>
          </CardHeader>
          <CardBody>
            <SimpleGrid columns={[2, 3, 5]} spacing={3}>
              {Object.entries(statistics.tasksByColumn).map(([column, count]) => (
                <VStack key={column} p={3} bg="gray.50" borderRadius="md" spacing={1}>
                  <Badge 
                    colorScheme={getColumnColor(column)} 
                    fontSize="lg" 
                    px={3} 
                    py={1}
                    borderRadius="full"
                  >
                    {count}
                  </Badge>
                  <Text fontSize="xs" fontWeight="bold" textTransform="capitalize">
                    {column}
                  </Text>
                </VStack>
              ))}
            </SimpleGrid>
          </CardBody>
        </Card>
      )}

      {/* Top Contributors */}
      {statistics.topContributors.length > 0 && (
        <Card shadow="md">
          <CardHeader>
            <HStack>
              <Icon as={FaTrophy} color="yellow.500" />
              <Heading size="sm">Top Contributors</Heading>
            </HStack>
          </CardHeader>
          <CardBody>
            <VStack align="stretch" spacing={3}>
              {statistics.topContributors.map((contributor, index) => (
                <HStack 
                  key={contributor.userId} 
                  p={3} 
                  bg="gray.50" 
                  borderRadius="md"
                  justify="space-between"
                >
                  <HStack spacing={3}>
                    <Badge 
                      colorScheme={index === 0 ? 'yellow' : 'gray'}
                      fontSize="md"
                      px={2}
                    >
                      #{index + 1}
                    </Badge>
                    <Avatar size="sm" name={contributor.name} src={contributor.image} />
                    <VStack align="start" spacing={0}>
                      <Text fontWeight="bold" fontSize="sm">{contributor.name}</Text>
                      <Text fontSize="xs" color="gray.500">{contributor.email}</Text>
                    </VStack>
                  </HStack>
                  <HStack spacing={4}>
                    <VStack spacing={0}>
                      <Text fontSize="xs" color="gray.500">Tasks</Text>
                      <Badge colorScheme="blue" fontSize="md">
                        {contributor.taskCount}
                      </Badge>
                    </VStack>
                    <VStack spacing={0}>
                      <Text fontSize="xs" color="gray.500">Avg Score</Text>
                      <Badge 
                        colorScheme={getScoreColor(contributor.avgScore)} 
                        fontSize="md"
                      >
                        {formatNumber(contributor.avgScore, 1)}
                      </Badge>
                    </VStack>
                  </HStack>
                </HStack>
              ))}
            </VStack>
          </CardBody>
        </Card>
      )}

      {/* Tasks by Category */}
      {Object.keys(statistics.tasksByCategory).length > 0 && (
        <Card shadow="md">
          <CardHeader>
            <Heading size="sm">🏷️ Tasks by Category</Heading>
          </CardHeader>
          <CardBody>
            <SimpleGrid columns={[2, 3, 4]} spacing={3}>
              {Object.entries(statistics.tasksByCategory)
                .sort(([, a], [, b]) => b - a)
                .map(([category, count]) => (
                  <Tooltip key={category} label={`${count} tasks`}>
                    <Box p={3} bg="gray.50" borderRadius="md" cursor="pointer" _hover={{ bg: 'gray.100' }}>
                      <VStack spacing={1}>
                        <Text fontSize="2xl" fontWeight="bold">{count}</Text>
                        <Text fontSize="xs" fontWeight="medium" textAlign="center" noOfLines={2}>
                          {category}
                        </Text>
                      </VStack>
                    </Box>
                  </Tooltip>
                ))}
            </SimpleGrid>
          </CardBody>
        </Card>
      )}

      {/* Tasks by Label */}
      {Object.keys(statistics.tasksByLabel).length > 0 && (
        <Card shadow="md">
          <CardHeader>
            <Heading size="sm">🔖 Tasks by Label</Heading>
          </CardHeader>
          <CardBody>
            <HStack spacing={3} flexWrap="wrap">
              {Object.entries(statistics.tasksByLabel).map(([label, count]) => (
                <Badge 
                  key={label} 
                  colorScheme={label === 'completed' ? 'green' : label === 'urgent' ? 'red' : 'blue'}
                  fontSize="md"
                  px={4}
                  py={2}
                  borderRadius="full"
                >
                  {label}: {count}
                </Badge>
              ))}
            </HStack>
          </CardBody>
        </Card>
      )}

      {/* Charts Section */}
      <Grid templateColumns={{ base: '1fr', md: '1fr 2fr' }} gap={4}>
        <Card shadow="md">
          <CardHeader>
            <Heading size="sm">Distribution Chart</Heading>
          </CardHeader>
          <CardBody>
            <ChartsPieKanban />
          </CardBody>
        </Card>

        <Card shadow="md">
          <CardHeader>
            <Heading size="sm">Performance Trend</Heading>
          </CardHeader>
          <CardBody>
            <ChartsBarKanban />
          </CardBody>
        </Card>
      </Grid>

      {/* Team Members */}
      {dataKanban?.usersDisplay?.length > 0 && (
        <Card shadow="md">
          <CardHeader>
            <HStack>
              <Icon as={FaUsers} color="blue.500" />
              <Heading size="sm">Team Members ({dataKanban.usersDisplay.length})</Heading>
            </HStack>
          </CardHeader>
          <CardBody>
            <AvatarGroup size="md" max={20}>
              {dataKanban.usersDisplay
                .sort((a, b) => (a.name || a.email).localeCompare(b.name || b.email))
                .map((user, index) => (
                  <Tooltip key={index} label={user.name || user.email}>
                    <Avatar name={user.name || user.email} src={user.image} />
                  </Tooltip>
                ))}
            </AvatarGroup>
          </CardBody>
        </Card>
      )}
    </Stack>
  );
}

export default ManagementScoreTaskDetailV2;